import React, { FC, Fragment } from 'react';
import { Box, Checkbox, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import { Facility, FacilityUnitRate } from '@bechtel/types';
import { NetworkStatus } from '@apollo/client';
import { FieldArray, FieldProps } from 'formik';
import { findIndex, get, map, some } from 'lodash';
import { useAppUser, ErrorRetry, ListSelectionStyled, SubListSelectionStyled } from '@bechtel/client-common';
import { useProjectFacilityQuery } from '~/user-admin/hooks/use-facility';
import { Trans } from '@lingui/macro';

const FacilityList: FC<FieldProps> = ({ form }) => {
  const { user } = useAppUser();
  const { loading, error, data, refetch, networkStatus } = useProjectFacilityQuery(
    user?.project?.id ? user.project.id : ''
  );
  const facilityUnitRateList: FacilityUnitRate[] = get(data, 'projectConfigById.facilityUnitRates', []);
  const facilityList: Facility[] = map(facilityUnitRateList, (facilityUnitRate) => facilityUnitRate.facility);

  return (
    <Fragment>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }} data-testid="loading">
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!loading && error && (
        <Box data-testid="retry">
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading Facilities. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </Box>
      )}
      {!loading && !error && (
        <Box data-testid="success" flexDirection={{ sm: 'column' }}>
          <SubListSelectionStyled
            onClick={() => {
              if (form.values.facilities?.length !== facilityList.length) {
                form.setFieldValue('facilities', facilityList);
              } else {
                form.setFieldValue('facilities', []);
              }
            }}
          >
            <Box alignItems={{ sm: 'center' }}>
              <Box mr={{ sm: '500' }}>
                <Checkbox
                  checked={form.values.facilities?.length === facilityList.length}
                  indeterminate={
                    form.values.facilities?.length > 0 && form.values.facilities?.length < facilityList.length
                  }
                  readOnly
                />
              </Box>
              <Text size="400" weight="600" color="bechtel.grey.1000">
                <Trans>Facility</Trans>
              </Text>
            </Box>
            {form.values.facilities && form.values.facilities.length > 0 && (
              <Text size="400" color="bechtel.grey.1000">
                {form.values.facilities.length} <Trans>Selected</Trans>
              </Text>
            )}
          </SubListSelectionStyled>
          <FieldArray name="facilities">
            {(arrayHelpers) => {
              return (
                <>
                  {facilityList &&
                    facilityList.map((facility) => {
                      return (
                        <ListSelectionStyled
                          key={facility.id}
                          data-testid={`facility-list-${facility.id}`}
                          onClick={() => {
                            const selected = findIndex(form.values.facilities, (item: Facility) => {
                              return item.id === facility.id;
                            });
                            if (selected !== -1) {
                              arrayHelpers.remove(selected);
                            } else {
                              arrayHelpers.push(facility);
                            }
                          }}
                        >
                          <Box mr={{ sm: '500' }}>
                            <Checkbox
                              checked={some(form.values.facilities, (item: Facility) => {
                                return item.id === facility.id;
                              })}
                              readOnly
                            />
                          </Box>
                          <Text size="400" color="bechtel.grey.1000">
                            {facility.name}
                          </Text>
                        </ListSelectionStyled>
                      );
                    })}
                </>
              );
            }}
          </FieldArray>
        </Box>
      )}
    </Fragment>
  );
};

export default FacilityList;
